import { Target, BarChart3 } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { cn } from '@/utils/cn'
import { formFieldInputClass, formFieldLabelClass } from '@/components/shared/formFieldClasses'
import { SERVICE_INTEREST_CATEGORIES } from '@/constants/serviceCategories'
import { EcommercePlatformField } from '@/components/shared/EcommercePlatformField'
import { EcommerceSegmentField } from '@/components/shared/EcommerceSegmentField'
import { LeadSourceSelect } from '@/components/shared/LeadSourceSelect'
import type { CloserLeadFormData } from '@/types'

const modelos = ['B2B', 'B2C', 'B2B2C', 'Marketplace']
const faturamentos = ['< 20k', '20k - 50k', '50k - 100k', '100k - 300k', '300k - 500k', '500k - 1M', '1M - 5M', '5M+']

interface IntelligenceSectionProps {
  formData: CloserLeadFormData
  onFieldChange: (field: string, value: any) => void
  errors?: Record<string, string>
}

function SelectField({ label, value, onChange, options, placeholder = 'Selecione...', disabled = false, hint }: {
  label: string; value: string; onChange: (v: string) => void; options: string[]; placeholder?: string
  disabled?: boolean; hint?: string
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <label className={formFieldLabelClass} style={{ fontSize: 'var(--font-size-label)' }}>
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className={cn(formFieldInputClass, 'min-h-[38px] px-3 py-2 text-[13px] cursor-pointer disabled:opacity-60')}
      >
        <option value=''>{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {hint && <span className='text-[10px] text-text-muted'>{hint}</span>}
    </div>
  )
}

export function IntelligenceSection({ formData, onFieldChange, errors = {} }: IntelligenceSectionProps) {
  const handleServicoToggle = (servico: string) => {
    const current = formData.servicos || []
    const updated = current.includes(servico)
      ? current.filter(s => s !== servico)
      : [...current, servico]
    onFieldChange('servicos', updated)
  }

  return (
    <div className='space-y-6'>
      {/* Qualificação */}
      <div className='space-y-4'>
        <div className='flex items-center gap-2 mb-4'>
          <div className='p-1.5 rounded-lg bg-accent/10 text-accent'>
            <Target size={16} />
          </div>
          <span className='text-xs font-bold uppercase tracking-[0.15em] text-text-muted'>
            Qualificação & Segmento
          </span>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <EcommercePlatformField
            value={formData.plataforma}
            onChange={(v) => onFieldChange('plataforma', v)}
            error={errors.plataforma}
          />
          <EcommerceSegmentField
            value={formData.segmento}
            onChange={(v) => onFieldChange('segmento', v)}
            error={errors.segmento}
          />
          <SelectField
            label='Modelo'
            value={formData.modelo}
            onChange={v => onFieldChange('modelo', v)}
            options={modelos}
          />
          <LeadSourceSelect
            value={formData.origem}
            onChange={(v) => onFieldChange('origem', v)}
            error={errors.origem}
          />
        </div>
      </div>

      {/* Inteligência Comercial */}
      <div className='space-y-4'>
        <div className='flex items-center gap-2 mb-4'>
          <div className='p-1.5 rounded-lg bg-accent/10 text-accent'>
            <BarChart3 size={16} />
          </div>
          <span className='text-xs font-bold uppercase tracking-[0.15em] text-text-muted'>
            Inteligência Comercial
          </span>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <SelectField
            label='Faturamento'
            value={formData.faturamento}
            onChange={v => onFieldChange('faturamento', v)}
            options={faturamentos}
          />
          <Input
            label='Acessos Orgânicos'
            value={formData.trafego}
            onChange={e => onFieldChange('trafego', e.target.value)}
            placeholder='Ex: 50k/mês'
          />
        </div>

        {/* Serviços Multi-Select */}
        <div className='flex flex-col gap-1.5'>
          <label className='font-medium text-text-muted uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
            Serviços de Interesse
          </label>
          <div className='flex flex-wrap gap-2'>
            {SERVICE_INTEREST_CATEGORIES.map(s => (
              <button
                key={s}
                type='button'
                onClick={() => handleServicoToggle(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border transition-all ${
                  (formData.servicos || []).includes(s)
                    ? 'bg-accent/20 text-accent border-accent/30'
                    : 'bg-[var(--glass-bg)] text-[var(--text-muted)] border-[var(--glass-border)] hover:border-accent/20'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default IntelligenceSection
